/**
 * Pulsing rust halo around `[data-onboarding-target="<id>"]`, portaled to
 * document.body so it sits above all chrome. Re-measured on resize, scroll,
 * a ResizeObserver on the target and a cheap periodic rAF. Pointer-events
 * off: decoration, never an interactive layer.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { targetSelector } from "./canvas";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  /** Follows the target's own corner radius so pills and boxes both read. */
  radius: number;
}

const HALO_PADDING = 8;
const MIN_HALO_RADIUS = 14;

export function TourSpotlight({
  target,
  emphasisTick,
}: {
  target: string | null;
  /** Bump to replay the one-shot "look here" pulse. */
  emphasisTick: number;
}) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [emphasizing, setEmphasizing] = useState(false);
  const lastTickRef = useRef(emphasisTick);
  useEffect(() => {
    if (emphasisTick === lastTickRef.current) return;
    lastTickRef.current = emphasisTick;
    setEmphasizing(true);
    const t = window.setTimeout(() => setEmphasizing(false), 1200);
    return () => window.clearTimeout(t);
  }, [emphasisTick]);

  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    const selector = targetSelector(target);
    let el = document.querySelector<HTMLElement>(selector);

    // The target may not be in the DOM yet (chrome still animating in), so
    // the observer binds lazily inside `measure`.
    let observer: ResizeObserver | null = null;
    let observedEl: HTMLElement | null = null;

    const measure = () => {
      el = document.querySelector<HTMLElement>(selector);
      if (observer && el !== observedEl) {
        observer.disconnect();
        if (el) observer.observe(el);
        observedEl = el;
      }
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const computed = window.getComputedStyle(el);
      const parsed = parseFloat(computed.borderTopLeftRadius || "0") || 0;
      const maxRadius = Math.min(r.width, r.height) / 2 + HALO_PADDING;
      const radius = Math.min(
        Math.max(parsed + HALO_PADDING, MIN_HALO_RADIUS),
        maxRadius,
      );
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        radius,
      });
    };

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measure());
    }

    const initial = window.setTimeout(measure, 0);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    let raf = 0;
    const tickStart = performance.now();
    const loop = (t: number) => {
      if (t - tickStart > 0 && (t - tickStart) % 250 < 16) measure();
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    return () => {
      window.clearTimeout(initial);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      observer?.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [target]);

  if (!rect || !target) return null;

  return createPortal(
    <>
      <style>{SPOTLIGHT_STYLES}</style>
      <div
        aria-hidden
        className={
          emphasizing
            ? "_co_onb_spotlight _co_onb_spotlight_emphasis"
            : "_co_onb_spotlight"
        }
        style={{
          position: "fixed",
          top: rect.top - HALO_PADDING,
          left: rect.left - HALO_PADDING,
          width: rect.width + HALO_PADDING * 2,
          height: rect.height + HALO_PADDING * 2,
          borderRadius: rect.radius,
          pointerEvents: "none",
          zIndex: 60,
        }}
      />
    </>,
    document.body,
  );
}

const SPOTLIGHT_STYLES = `
  ._co_onb_spotlight {
    box-shadow:
      0 0 0 1.5px #B86E49,
      0 0 0 6px rgba(184, 110, 73, 0.18),
      0 0 24px 4px rgba(184, 110, 73, 0.36);
    animation: _co_onb_spotlight_breathe 1.6s ease-in-out infinite;
    will-change: opacity, transform;
  }
  @keyframes _co_onb_spotlight_breathe {
    0%, 100% { opacity: 0.85; transform: scale(1);    }
    50%      { opacity: 1;    transform: scale(1.04); }
  }
  @keyframes _co_onb_spotlight_emphasis {
    0% {
      opacity: 1;
      transform: scale(1);
      box-shadow:
        0 0 0 1.5px #B86E49,
        0 0 0 6px rgba(184, 110, 73, 0.18),
        0 0 24px 4px rgba(184, 110, 73, 0.36);
    }
    35% {
      opacity: 1;
      transform: scale(1.55);
      box-shadow:
        0 0 0 5px #B86E49,
        0 0 0 28px rgba(184, 110, 73, 0.32),
        0 0 100px 32px rgba(184, 110, 73, 0.7);
    }
    55% {
      opacity: 1;
      transform: scale(1.35);
      box-shadow:
        0 0 0 4px #B86E49,
        0 0 0 22px rgba(184, 110, 73, 0.26),
        0 0 80px 22px rgba(184, 110, 73, 0.55);
    }
    100% {
      opacity: 0.85;
      transform: scale(1);
      box-shadow:
        0 0 0 1.5px #B86E49,
        0 0 0 6px rgba(184, 110, 73, 0.18),
        0 0 24px 4px rgba(184, 110, 73, 0.36);
    }
  }
  ._co_onb_spotlight_emphasis {
    animation: _co_onb_spotlight_emphasis 1200ms cubic-bezier(0.2, 0, 0, 1) !important;
  }
  @media (prefers-reduced-motion: reduce) {
    ._co_onb_spotlight,
    ._co_onb_spotlight_emphasis { animation: none; opacity: 1; }
  }
`;
